import { useId, useRef, useState, type FormEvent } from 'react';
import { CheckCircle2, Eye, EyeOff, KeyRound } from 'lucide-react';
import { authErrorMessage, resetPassword } from '../auth/authClient.ts';
import { getAuthStore } from '../auth/authStore.ts';
import { clearResetToken, peekResetToken } from '../auth/resetToken.ts';
import { useUi } from '../App.tsx';
import { Button, Field, Panel, inputCls } from '../ui/kit.tsx';
import { tr } from '../i18n/i18n.ts';

const MIN = 10;

/** Välj nytt lösenord från återställningslänken. Token finns bara i minnet (auth/resetToken.ts). */
export function ResetPasswordView() {
  const ui = useUi();
  const uid = useId();
  const [token] = useState(peekResetToken);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ pw?: string; pw2?: string }>({});
  const [done, setDone] = useState(false);
  const submitting = useRef(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (submitting.current || !token) return;
    const fe: typeof fieldError = {};
    if (pw.length < MIN) fe.pw = tr(`Lösenordet måste ha minst ${MIN} tecken.`, `The password must have at least ${MIN} characters.`);
    else if (pw !== pw2) fe.pw2 = tr('Lösenorden är inte lika.', 'The passwords do not match.');
    setFieldError(fe);
    setError(null);
    if (fe.pw || fe.pw2) return;
    submitting.current = true;
    setBusy(true);
    try {
      // Samma ursprung: länken öppnas alltid på serverns egen webbadress.
      await resetPassword('', token, pw);
      clearResetToken();
      setPw('');
      setPw2('');
      setDone(true);
      // Servern har återkallat alla sessioner – även en här. Uppdatera kontostatus utan att radera något.
      void getAuthStore().verify();
    } catch (err) {
      setError(authErrorMessage(err, 'reset'));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-5 px-4 py-6">
      <h1 className="text-2xl font-semibold tracking-tight">{tr('Välj nytt lösenord', 'Choose a new password')}</h1>
      <Panel className="grid gap-4 p-5">
        {done ? (
          <div className="grid gap-4 text-sm" role="status">
            <p className="flex gap-2">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-accent" aria-hidden />
              <span>{tr('Lösenordet är bytt. Alla dina enheter har loggats ut. Logga in med det nya lösenordet.', 'The password has been changed. All your devices have been signed out. Sign in with the new password.')}</span>
            </p>
            <Button variant="primary" onClick={() => ui.openAuth('login')}>
              {tr('Logga in', 'Sign in')}
            </Button>
          </div>
        ) : !token ? (
          <div className="grid gap-4 text-sm">
            <p role="alert">{tr('Länken saknar giltig kod eller har redan använts på den här sidan. Begär en ny länk via Logga in → Glömt lösenordet.', 'The link has no valid code or has already been used on this page. Request a new link via Sign in → Forgot password.')}</p>
            <Button onClick={() => ui.openAuth('login')}>{tr('Logga in', 'Sign in')}</Button>
          </div>
        ) : (
          <form className="grid gap-4" method="post" noValidate onSubmit={(e) => void submit(e)} aria-busy={busy}>
            <Field label={tr('Nytt lösenord', 'New password')} htmlFor={`${uid}-pw`} error={fieldError.pw} hint={tr(`Minst ${MIN} tecken.`, `At least ${MIN} characters.`)}>
              <div className="flex gap-2">
                <input
                  id={`${uid}-pw`}
                  className={inputCls}
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  aria-invalid={!!fieldError.pw}
                  maxLength={256}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  className="grid min-h-11 min-w-11 shrink-0 place-items-center rounded-lg border border-border-strong text-muted hover:text-text"
                  onClick={() => setShow((v) => !v)}
                  aria-pressed={show}
                  aria-label={show ? tr('Dölj lösenord', 'Hide password') : tr('Visa lösenord', 'Show password')}
                  title={show ? tr('Dölj lösenord', 'Hide password') : tr('Visa lösenord', 'Show password')}
                >
                  {show ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
                </button>
              </div>
            </Field>
            <Field label={tr('Upprepa lösenordet', 'Repeat the password')} htmlFor={`${uid}-pw2`} error={fieldError.pw2}>
              <input
                id={`${uid}-pw2`}
                className={inputCls}
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                aria-invalid={!!fieldError.pw2}
                maxLength={256}
                required
              />
            </Field>
            <p className="text-sm text-muted">{tr('När lösenordet är bytt loggas alla dina enheter ut. Kalenderdata på enheterna raderas inte.', 'When the password has been changed, all your devices are signed out. Calendar data on the devices is not deleted.')}</p>
            {error && (
              <p className="rounded-lg border border-risk/60 bg-risk-bg px-3 py-2 text-sm" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" disabled={busy} icon={<KeyRound size={16} aria-hidden />}>
              {busy ? tr('Sparar…', 'Saving…') : tr('Spara nytt lösenord', 'Save new password')}
            </Button>
          </form>
        )}
      </Panel>
    </div>
  );
}
